import {Avatar} from 'shells/avatar/avatar';
import {SelectionModal} from 'shells/selection-modal/selection-modal';

import type {
    GetUsers_users_edges_node as User,
    UserQuotaFragment_quotas as Quota,
    UserQuotaFragment as UserQuota,
} from 'nutshell-graphql-types';

import {useGetUsers} from '../../core/select/hooks';
import type {Option} from '../../scheduler/assignment/users-teams-selection-modal.tsx';

import {useDuplicateQuota} from './graphql/hooks/use-duplicate-quota';

type Props = {
    isOpen: boolean;
    quota: Quota;
    user: UserQuota;
    existingUsers: UserQuota[];
    setScrollToUserId?: (userId: string) => void;
    onDuplicateStart?: (userIds: string[]) => void;
    onClose: () => void;
};

export function DuplicateToUsersModal(props: Props) {
    const {users} = useGetUsers();
    const {duplicateQuota} = useDuplicateQuota();

    const options = buildOptionsList(
        users,
        props.existingUsers,
        props.quota.fiscalYear,
        props.user.id
    );

    return (
        <SelectionModal
            title={`Copy ${props.user.name}'s ${props.quota.fiscalYear} quota for`}
            nothingFoundState='No users selected'
            isOpen={props.isOpen}
            onClose={props.onClose}
            buttonConfig={{
                type: 'save',
                label: 'Create quotas',
                savingText: 'Creating quotas...',
                successText: 'Quotas created!',
                onSelection: async (selected) => {
                    const userIds = selected.map((option) => option.value);

                    if (props.onDuplicateStart) {
                        props.onDuplicateStart(userIds);
                    }

                    return duplicateQuota({
                        userIds,
                        duplicateFromUserId: props.user.id,
                        fiscalYear: props.quota.fiscalYear,
                    }).then(() => {
                        if (props.setScrollToUserId && selected[0]?.value) {
                            props.setScrollToUserId(selected[0].value);
                        }

                        props.onClose();
                    });
                },
            }}
            options={options}
            selectPlaceholder='Search for a user'
        />
    );
}

function buildOptionsList(
    users: User[],
    existingUserQuotas: UserQuota[],
    fiscalYear: number,
    currentUserId: string
): Option[] {
    const excludedUserIds = getUserIdsWithQuotaForFiscalYear(existingUserQuotas, fiscalYear);

    return users
        .filter((user) => user.id !== currentUserId && !excludedUserIds.has(user.id))
        .map((user) => userToSelectionOption(user));
}

function getUserIdsWithQuotaForFiscalYear(
    existingUserQuotas: UserQuota[],
    fiscalYear: number
): Set<string> {
    return new Set(
        existingUserQuotas
            .filter((user) => user.quotas.some((quota) => quota.fiscalYear === fiscalYear))
            .map((user) => user.id)
    );
}

function userToSelectionOption(user: User): Option {
    return {
        value: user.id,
        label: user.name,
        iconNode: (
            <Avatar
                size={23}
                avatarUrl={user.avatarUrl}
                initials={user.initials}
                circular={true}
                type='users'
            />
        ),
        metaData: 'user',
    };
}
